import React, { useCallback } from "react";
import { ModalSignUpProps } from "domains/auth/components/ModalSignUp";
import { useCreateNewModelModalContext } from "domains/models/contexts/CreateNewModelModalProvider";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Button, { ButtonProps } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import {
  Box,
  Center,
  Collapse,
  Grid,
  GridProps,
  HStack,
  StackProps,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";

import ModalEnhanceSelect from "../ModalEnhanceSelect";
import { Section, SectionAction } from "../SectionBase";

export type ToolName =
  | "Train"
  | "Compose"
  | "Upload"
  | "Images"
  | "Videos"
  | "3D"
  | "Textures"
  | "Skyboxes"
  | "Live"
  | "Expand"
  | "Edit With Prompts"
  | "Remove Background"
  | "Enhance"
  | "Retouch"
  | "Vectorize"
  | "Pixelate"
  | "Restyle";

export default function SectionTools() {
  const { isUnsigned } = useUserContext();
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Section
      title="Tools"
      action={
        <SectionAction
          onClick={() => {
            Track(
              isOpen
                ? AnalyticsEvents.Navigation.ClickedViewAllToolsMinimized
                : AnalyticsEvents.Navigation.ClickedViewAllToolsDisplayAll,
              {
                persona: "default",
                userIsAuthenticated: !isUnsigned,
                context: "home",
              }
            );
            onToggle();
          }}
          rightIcon={
            <Icon id={isOpen ? "Ui/ChevronUpSm" : "Ui/ChevronDownSm"} />
          }
        >
          View All
        </SectionAction>
      }
    >
      <Tools isMinimized={!isOpen} context="home" />
    </Section>
  );
}

// ------------------------------------

interface ToolsProps {
  /**
   * The context in which the Tools component is being displayed.
   * This is used for the tracking in PostHog.
   */
  context: "home" | "topbar";
  isMinimized?: boolean;
  isBlurred?: boolean;
  isCompact?: boolean;
  onButtonClick?: () => void;
}

export function Tools({
  context,
  isMinimized,
  isBlurred,
  isCompact,
  onButtonClick,
}: ToolsProps) {
  const { isUnsigned, openSignUpModal } = useUserContext();
  const { hasNoProject } = useTeamContext();
  const { openUpload: openUploadModelModal } = useCreateNewModelModalContext();
  const {
    onOpen: onEnhanceModalOpen,
    isOpen: isEnhanceModalOpen,
    onClose: onEnhanceModalClose,
  } = useDisclosure();

  // ----------------------------------

  const handleButtonClick = useCallback(
    (
      {
        variant,
        toolName,
        signedCallback,
      }: {
        /** Which modal to open */
        variant: ModalSignUpProps["variant"];
        toolName: ToolName;
        signedCallback?: () => void;
      },
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      let eventName: string | undefined;
      switch (toolName) {
        case "Train":
          eventName = AnalyticsEvents.Navigation.ClickedModelTrain;
          break;
        case "Compose":
          eventName = AnalyticsEvents.Navigation.ClickedModelCompose;
          break;
        case "Upload":
          eventName = AnalyticsEvents.Navigation.ClickedModelUpload;
          break;
        case "Images":
          eventName = AnalyticsEvents.Navigation.ClickedGenerateImages;
          break;
        case "Videos":
          eventName = AnalyticsEvents.Navigation.ClickedGenerateVideo;
          break;
        case "3D":
          eventName = AnalyticsEvents.Navigation.ClickedGenerate3D;
          break;
        case "Textures":
          eventName = AnalyticsEvents.Navigation.ClickedGenerateTextures;
          break;
        case "Skyboxes":
          eventName = AnalyticsEvents.Navigation.ClickedGenerateSkyboxes;
          break;
        case "Live":
          eventName = AnalyticsEvents.Navigation.ClickedGenerateLive;
          break;
        case "Expand":
          eventName = AnalyticsEvents.Navigation.ClickedEditReframe;
          break;
        case "Edit With Prompts":
          eventName = AnalyticsEvents.Navigation.ClickedEditWithPrompt;
          break;
        case "Remove Background":
          eventName = AnalyticsEvents.Navigation.ClickedEditRemoveBackground;
          break;
        case "Enhance":
          eventName = AnalyticsEvents.Navigation.ClickedEditEnhance;
          break;
        case "Retouch":
          eventName = AnalyticsEvents.Navigation.ClickedEditRetouch;
          break;
        case "Vectorize":
          eventName = AnalyticsEvents.Navigation.ClickedEditVectorize;
          break;
        case "Pixelate":
          eventName = AnalyticsEvents.Navigation.ClickedEditPixelate;
          break;
        case "Restyle":
          eventName = AnalyticsEvents.Navigation.ClickedEditRestyle;
          break;
        default:
          eventName = undefined;
          break;
      }
      if (eventName) {
        Track(eventName, {
          persona: "default",
          userIsAuthenticated: !isUnsigned,
          toolName,
          modalVariant: variant,
          context,
        });
      }
      if (isUnsigned) {
        e.preventDefault();
        onButtonClick?.();
        openSignUpModal(variant);
      } else {
        onButtonClick?.();
        signedCallback?.();
      }
    },
    [context, isUnsigned, onButtonClick, openSignUpModal]
  );

  /**
   * Factory function to create a click handler for a tool.
   */
  const getToolClickHandler = useCallback(
    ({
      variant,
      toolName,
      signedCallback,
    }: {
      variant: ModalSignUpProps["variant"];
      toolName: ToolName;
      signedCallback?: () => void;
    }) => handleButtonClick.bind(null, { variant, toolName, signedCallback }),
    [handleButtonClick]
  );

  // ----------------------------------

  return (
    <>
      <Grid
        gap={isCompact ? 6 : 3}
        templateColumns={[
          "1fr",
          "1fr",
          "1fr 1fr",
          "1fr 1fr 1fr",
          "1fr 1fr 2fr",
        ]}
        templateAreas={[
          `"a" "b" "c"`,
          `"a" "b" "c"`,
          `"a b" "c c"`,
          `"a b c"`,
          `"a b c c"`,
        ]}
        w="full"
      >
        <ListColumn
          title="TRAIN"
          gridArea="a"
          isBlurred={isBlurred}
          isMinimized={isMinimized}
          isCompact={isCompact}
        >
          <ListItem
            title="Train"
            description="Train your own models to generate consistent assets."
            icon={<Icon id="Domains/Tools/Train" h="13px" />}
            internalLink="/models/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "train",
              toolName: "Train",
            })}
            data-testid="create-menu-train"
          />

          <ListItem
            title="Compose"
            description="Combine custom models to create unique, distinctive styles."
            icon={<Icon id="Domains/Tools/Blend" h="15px" />}
            internalLink="/compose/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "train",
              toolName: "Compose",
            })}
            data-testid="create-menu-compose"
          />

          <ListItem
            title="Upload"
            description="Import external models and use them instantly in Scenario."
            icon={<Icon id="Ui/Upload" h="16px" />}
            onClick={getToolClickHandler({
              variant: "train",
              toolName: "Upload",
              signedCallback: openUploadModelModal,
            })}
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            data-testid="create-menu-upload"
          />
        </ListColumn>

        <ListColumn
          title="GENERATE"
          gridArea="b"
          isBlurred={isBlurred}
          isMinimized={isMinimized}
          isCompact={isCompact}
        >
          <ListItem
            title="Images"
            description="Create consistent visuals using custom settings and models."
            icon={<Icon id="Domains/Tools/Generate" h="16px" />}
            internalLink="/images/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "generate",
              toolName: "Images",
            })}
            data-testid="create-menu-images"
          />

          <ListItem
            title="Videos"
            isNew
            description="Animate your images with guided motion and camera control."
            icon={<Icon id="Nav/Video/Outline" h="14px" />}
            internalLink="/videos/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "generate",
              toolName: "Videos",
            })}
            data-testid="create-menu-videos"
          />

          <ListItem
            title="3D"
            isNew
            description="Convert images into textured meshes for your 3D workflow."
            icon={<Icon id="Nav/3D/Outline" h="18px" />}
            internalLink="/3d/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "generate",
              toolName: "3D",
            })}
            data-testid="create-menu-3d"
          />

          <ListItem
            title="Textures"
            description="Create seamless materials from a prompt or reference image."
            icon={<Icon id="Domains/Tools/Textures" h="16px" />}
            internalLink="/textures/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "generate",
              toolName: "Textures",
            })}
            data-testid="create-menu-textures"
          />

          <ListItem
            title="Skyboxes"
            description="Design 360 panoramas for 3D environments and games."
            icon={<Icon id="Domains/Tools/Skyboxes" h="16px" />}
            internalLink="/skyboxes/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "generate",
              toolName: "Skyboxes",
            })}
            data-testid="create-menu-skyboxes"
          />

          <ListItem
            title="Live"
            description="Create in real time with AI-powered sketch-to-image rendering."
            icon={<Icon id="Domains/Canvas/Live" h="18px" mt="-2px" />}
            internalLink={{
              pathname: "/canvas/new",
              query: {
                live: "true",
              },
            }}
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "generate",
              toolName: "Live",
            })}
            data-testid="create-menu-live"
          />
        </ListColumn>

        <ListColumn
          title="EDIT"
          gridArea="c"
          gridProps={{
            templateColumns: ["1fr", "1fr", "1fr 1fr", "1fr", "1fr 1fr"],
            templateAreas: [
              `"a" "b" "c" "d" "e" "f" "g" "h"`,
              `"a" "b" "c" "d" "e" "f" "g" "h"`,
              `"a e" "b f" "c g" "d h"`,
              `"a" "b" "c" "d" "e" "f" "g" "h"`,
              `"a f" "b g" "c h" "d z" "e z"`,
            ],
            gridColumnGap: isCompact && context === "topbar" ? 6 : undefined,
          }}
          isBlurred={isBlurred}
          isMinimized={isMinimized}
          isCompact={isCompact}
        >
          <ListItem
            gridArea="a"
            title="Expand"
            description="Extend the boundaries of any image using AI outpainting."
            icon={<Icon id="Nav/Expand/Outline" h="16px" />}
            internalLink="/expand"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Expand",
            })}
            data-testid="create-menu-expand"
          />

          <ListItem
            gridArea="b"
            title="Edit with Prompts"
            isNew
            description="Edit images with plain-text instructions, no complex tools needed."
            icon={<Icon id="Nav/Prompt/Outline" h="18px" />}
            internalLink="/edit-with-prompts"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Edit With Prompts",
            })}
            data-testid="create-menu-edit-with-prompts"
          />

          <ListItem
            gridArea="c"
            title="Remove Background"
            description="Instantly remove or swap backgrounds, no masking required."
            icon={<Icon id="Domains/Tools/RemoveBackground" h="16px" />}
            internalLink="/background-remove"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Remove Background",
            })}
            data-testid="create-menu-remove-background"
          />

          <ListItem
            gridArea="d"
            title="Enhance"
            description="Upscale and sharpen assets for improved resolution and clarity."
            icon={<Icon id="Domains/Tools/Upscale" h="16px" />}
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Enhance",
              signedCallback: onEnhanceModalOpen,
            })}
            data-testid="create-menu-enhance"
          />

          <ListItem
            gridArea="e"
            title="Retouch"
            description="Adjust details or create precise variations via AI inpainting."
            icon={<Icon id="Domains/Tools/Canvas" h="16px" />}
            internalLink="/canvas/new"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Retouch",
            })}
            data-testid="create-menu-retouch"
          />

          <ListItem
            gridArea="f"
            title="Vectorize"
            description="Turn raster images into clean, scalable vector graphics."
            icon={<Icon id="Domains/Tools/Vectorize" h="16px" />}
            internalLink="/vectorize"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Vectorize",
            })}
            data-testid="create-menu-vectorize"
          />

          <ListItem
            gridArea="g"
            title="Pixelate"
            description="Stylize your images with a retro, pixel-art aesthetic."
            icon={<Icon id="Domains/Tools/Pixelate" h="16px" />}
            internalLink="/pixelate"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Pixelate",
            })}
            data-testid="create-menu-pixelate"
          />

          <ListItem
            gridArea="h"
            title="Restyle"
            description="Apply another image's style to transform a reference image."
            icon={<Icon id="Domains/Tools/Restyle" h="18px" />}
            internalLink="/restyle"
            isDisabled={hasNoProject}
            isBlurred={isBlurred}
            isCompact={isCompact}
            onClick={getToolClickHandler({
              variant: "edit",
              toolName: "Restyle",
            })}
            data-testid="create-menu-restyle"
          />
        </ListColumn>
      </Grid>

      <ModalEnhanceSelect
        isOpen={isEnhanceModalOpen}
        onClose={onEnhanceModalClose}
      />
    </>
  );
}

// ------------------------------------

interface ListColumnProps extends Omit<StackProps, "title"> {
  title: React.ReactNode;
  isMinimized?: boolean;
  isBlurred?: boolean;
  isCompact?: boolean;
  gridProps?: GridProps;
}

function ListColumn({
  title,
  isMinimized = false,
  isBlurred = false,
  isCompact = false,
  children,
  gridArea,
  gridProps,
  ...props
}: ListColumnProps) {
  return (
    <Box
      gridArea={gridArea}
      borderRadius="2xl"
      transition="height 0.3s linear"
      bgColor={
        (isCompact && "none") ||
        (isBlurred && "rgba(26, 26, 26, 0.9)") ||
        "backgroundTertiary.500"
      }
      data-outside-click-excluded="true"
      {...props}
    >
      <VStack pos="relative" align="stretch" p={isCompact ? 0 : 4} spacing={2}>
        <Text px={isCompact ? 0 : 4} size="title.xs">
          {title}
        </Text>

        <Collapse in={!isMinimized} startingHeight="150px">
          <Grid gap={isCompact ? 0 : 2} templateColumns="1fr" {...gridProps}>
            {children}
          </Grid>
        </Collapse>
      </VStack>
    </Box>
  );
}

// ------------------------------------

interface ListItemProps extends Omit<ButtonProps, "title"> {
  title: React.ReactNode;
  description: React.ReactNode;
  icon: React.ReactNode;
  isNew?: boolean;
  isPremium?: boolean;
  isBlurred?: boolean;
  isCompact?: boolean;
  gridArea?: EditGridArea;
}

function ListItem({
  title,
  description,
  icon,
  isNew = false,
  isPremium = false,
  isBlurred = false,
  isCompact = false,
  gridArea,
  ...props
}: ListItemProps) {
  return (
    <Tooltip isDisabled={!isPremium} label="Upgrade to access this feature">
      <Box gridArea={gridArea}>
        <Button
          display="block"
          variant="unstyled"
          w="full"
          h="auto"
          borderRadius="xl"
          _hover={{ bgColor: "background.500" }}
          cursor="pointer"
          bgColor={isBlurred || isCompact ? "none" : "backgroundTertiary.500"}
          textAlign="left"
          align="stretch"
          whiteSpace="normal"
          transition="none"
          px={isCompact ? 0 : 3}
          py={2}
          spacing={2}
          {...props}
          data-group
        >
          <HStack pos="relative" spacing={3}>
            <Box>
              <Center
                w="48px"
                h="48px"
                ml={isCompact ? 0 : -2}
                borderRadius="xl"
                bgColor={
                  isCompact ? "backgroundSecondary.500" : "background.500"
                }
              >
                {icon}
              </Center>
            </Box>

            <VStack align="stretch" flex={1} spacing={1}>
              <HStack>
                <Text size="body.bold.md">{title}</Text>
                {isNew && (
                  <Tag colorScheme="primary" size="sm" variant="subtle">
                    New
                  </Tag>
                )}
                {isPremium && <Icon id="Ui/Stars" color="primary.500" />}
              </HStack>

              {!isCompact && (
                <Text
                  sx={{
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    display: "-webkit-box",
                  }}
                  overflow="hidden"
                  minH="29px"
                  color="textTertiary"
                  lineHeight="1.2"
                  size="body.sm"
                >
                  {description}
                </Text>
              )}
            </VStack>

            <Center
              pos="absolute"
              top={0}
              right={-3}
              h="full"
              px={3}
              opacity={isCompact ? 1 : 0}
              borderRadius="md"
              _groupHover={isCompact ? {} : { opacity: 1 }}
              bgColor="background.500"
            >
              <Icon id="Ui/ChevronRight" h="12px" />
            </Center>
          </HStack>
        </Button>
      </Box>
    </Tooltip>
  );
}

// ------------------------------------

const EDIT_GRID_AREAS = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;
type EditGridArea = (typeof EDIT_GRID_AREAS)[number];
